<script setup lang="ts">
interface Props {
  modelValue: boolean
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'linkClick': []
}>()

const handleChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  emit('update:modelValue', target.checked)
}

const handleLabelClick = (event: MouseEvent) => {
  // Check if the clicked element is a link
  const target = event.target as HTMLElement
  if (target.tagName === 'A' || target.closest('a')) {
    event.preventDefault()
    emit('linkClick')
  }
}
</script>

<template>
  <label
    class="base-checkbox"
    :class="{ 'base-checkbox--disabled': disabled }"
    @click="handleLabelClick"
  >
    <input
      type="checkbox"
      class="base-checkbox__input"
      :checked="modelValue"
      :disabled="disabled"
      @change="handleChange"
    />
    <span class="base-checkbox__checkmark"></span>
    <span class="base-checkbox__label">
      <slot />
    </span>
  </label>
</template>

<style scoped>
.base-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  user-select: none;
  position: relative;
}

.base-checkbox--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Hide default checkbox */
.base-checkbox__input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Custom checkbox */
.base-checkbox__checkmark {
  position: relative;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 2px;
  border: 2px solid rgba(45, 82, 210, 0.2);
  border-radius: 4px;
  background-color: white;
  transition: all 0.2s ease;
}

.base-checkbox:hover .base-checkbox__checkmark {
  border-color: rgba(45, 82, 210, 0.4);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
}

.base-checkbox__input:checked ~ .base-checkbox__checkmark {
  background: linear-gradient(135deg, #B1FF8C 0%, #B1FF8C 100%);
  border-color: #B1FF8C;
  box-shadow: 0 2px 8px rgba(177, 255, 140, 0.3);
}

/* Checkmark icon */
.base-checkbox__checkmark::after {
  content: '';
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid #000000;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.base-checkbox__input:checked ~ .base-checkbox__checkmark::after {
  display: block;
}

/* Label text */
.base-checkbox__label {
  flex: 1;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(32, 48, 95, 0.9);
}

.base-checkbox--disabled .base-checkbox__label {
  color: rgba(32, 48, 95, 0.5);
}

/* Link styling within label */
.base-checkbox__label :deep(a) {
  color: #667eea;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.15s ease;
  font-weight: 500;
}

.base-checkbox__label :deep(a:hover) {
  color: #764ba2;
}

/* Focus state for accessibility */
.base-checkbox__input:focus-visible ~ .base-checkbox__checkmark {
  outline: 2px solid #667eea;
  outline-offset: 2px;
}
</style>
